import type { UserAccount, LoginRequest, LoginResponse, LoginSource } from '../types/auth';

/**
 * Backend API base URL
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Storage keys
 * 注意：accessToken 由後端的 HttpOnly Cookie 管理，不存在 localStorage
 */
const STORAGE_KEYS = {
  USER: 'auth_user',
} as const;

/**
 * Convert login source to API format
 */
function toLoginSource(provider: 'google' | 'apple'): LoginSource {
  return provider === 'google' ? 'Google' : 'Apple';
}

/**
 * Login with Google or Apple ID Token
 */
export async function loginWithIdToken(
  idToken: string,
  provider: 'google' | 'apple'
): Promise<UserAccount> {
  try {
    const loginSource = toLoginSource(provider);

    const response = await fetch(`${API_BASE_URL}/user/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // CSRF 防護：後端會擋掉沒有這個 header 的變更資料請求
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include', // 讓瀏覽器自動處理 Cookie
      body: JSON.stringify({
        idToken,
        loginSource,
      } as LoginRequest),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: '登入失敗' }));
      throw new Error(error.message || '登入失敗');
    }

    const result: LoginResponse = await response.json();

    // Check exit code
    if (result.exitCode !== 1) {
      throw new Error(result.message || '登入失敗');
    }

    const { data } = result;

    // Create user account object
    // 注意：accessToken 不存儲，認證完全交由後端設定的 HttpOnly Cookie 處理，
    // 避免 XSS 能透過 localStorage 讀到憑證
    const user: UserAccount = {
      userId: data.userId,
      nickname: data.nickname,
      avatar: data.avatar,
      loginSource: data.loginSource,
    };

    // 只存儲用戶顯示資訊（非敏感資料）
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));

    return user;
  } catch (error) {
    console.error('Login error:', error);
    throw handleAuthError(error);
  }
}

/**
 * Sign out
 * 呼叫後端 logout API，後端會清除 HttpOnly Cookie
 */
export async function signOut(): Promise<void> {
  try {
    // 呼叫後端登出 API（會清除 Cookie）
    await fetch(`${API_BASE_URL}/user/logout`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include', // 帶上 Cookie 讓後端知道要清除哪個 session
    });

    // 清除本地用戶資訊
    localStorage.removeItem(STORAGE_KEYS.USER);
  } catch (error) {
    console.error('Sign out error:', error);
    // 即使後端失敗，也清除本地資訊
    localStorage.removeItem(STORAGE_KEYS.USER);
  }
}

/**
 * Get current user from storage
 */
export function getCurrentUser(): UserAccount | null {
  const userJson = localStorage.getItem(STORAGE_KEYS.USER);
  if (!userJson) return null;

  try {
    return JSON.parse(userJson) as UserAccount;
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 * 注意：這只檢查本地狀態，實際認證由後端 Cookie 處理
 */
export function isAuthenticated(): boolean {
  return !!getCurrentUser();
}

/**
 * Handle auth errors
 */
function handleAuthError(error: unknown): Error {
  const message = error instanceof Error ? error.message : '';
  // Network errors
  if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
    return new Error('網路連線失敗，請稍後再試');
  }

  // Return original error or generic message
  return error instanceof Error ? error : new Error('登入時發生錯誤，請稍後再試');
}
